import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Download, Upload, CheckCircle2, AlertCircle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { importApi } from '../api/import';
import { cn } from '../lib/utils';

const getDiagnostics = (result) => result?.diagnostics || {};
const getWarnings = (result) => result?.warnings || result?.diagnostics?.warnings || [];
const getValidationErrors = (result) => result?.validation_errors || result?.diagnostics?.validation_errors || [];

const DiagnosticTile = ({ label, value, tone = 'slate' }) => {
  const toneClass = {
    slate: 'bg-slate-50 text-slate-900 border-slate-200',
    green: 'bg-green-50 text-green-900 border-green-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    purple: 'bg-purple-50 text-purple-900 border-purple-200'
  }[tone];

  return (
    <div className={cn('rounded-lg border p-3', toneClass)}>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="text-xs font-medium uppercase tracking-wide opacity-75">{label}</div>
    </div>
  );
};

export default function Import() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [validateOnly, setValidateOnly] = useState(false);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    setResult(null);

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
      } else {
        setError('Only .xlsx and .xls files are supported');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxSize: 50 * 1024 * 1024,
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);
    setResult(null);
    setProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return 90;
        return prev + 10;
      });
    }, 200);

    try {
      const response = await importApi.uploadFile(file, { validate_only: validateOnly });
      clearInterval(progressInterval);
      setProgress(100);

      setResult(response);
      if (response.success) {
        setFile(null);
      }
    } catch (err) {
      clearInterval(progressInterval);
      setError(err.response?.data?.error_message || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await importApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Import Opportunities</h1>
        <p className="mt-2 text-gray-600">
          Upload Excel file to import parts and volume forecasts
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Download Template */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Step 1: Download Template (Optional)</CardTitle>
            <CardDescription>
              Need a template? Download our Excel template with the correct format
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleDownloadTemplate} variant="outline" className="w-full sm:w-auto">
              <Download className="mr-2 h-4 w-4" />
              Download Excel Template
            </Button>
          </CardContent>
        </Card>

        {/* Step 2: Upload File */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Step 2: Upload Your File</CardTitle>
            <CardDescription>
              Drag and drop your Excel file or click to browse
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              {...getRootProps()}
              className={cn(
                'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
                isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
                importing && 'pointer-events-none opacity-60'
              )}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-2">
                {file ? (
                  <>
                    <FileSpreadsheet className="h-12 w-12 text-green-600" />
                    <p className="text-lg font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    {!importing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          setResult(null);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-gray-400" />
                    <p className="text-lg font-medium text-gray-900">
                      {isDragActive ? 'Drop file here' : 'Drag & Drop File Here'}
                    </p>
                    <p className="text-sm text-gray-500">or click to browse</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Supported: .xlsx, .xls | Max: 50MB
                    </p>
                  </>
                )}
              </div>
            </div>

            {importing && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">
                    {validateOnly ? 'Validating...' : 'Importing...'}
                  </span>
                  <span className="text-gray-500">{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {/* Options */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="validateOnly"
                  checked={validateOnly}
                  onCheckedChange={setValidateOnly}
                  disabled={importing}
                />
                <label
                  htmlFor="validateOnly"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Validate only (don't import)
                </label>
              </div>
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!file || importing}
              className="w-full"
              size="lg"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {validateOnly ? 'Validating...' : 'Importing...'}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {validateOnly ? 'Validate File' : 'Upload & Import'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success/Validation Results */}
        {result && (
          <Card className={result.success ? 'border-green-500' : 'border-red-500'}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.success ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-green-900">
                      {validateOnly ? 'Validation Passed' : 'Import Successful'}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-900">
                      {validateOnly ? 'Validation Failed' : 'Import Failed'}
                    </span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.success ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <DiagnosticTile
                      label="Parts processed"
                      value={result.parts_processed ?? getDiagnostics(result).unique_parts}
                      tone="slate"
                    />
                    <DiagnosticTile
                      label={validateOnly ? 'Parts to create' : 'Parts created'}
                      value={validateOnly ? getDiagnostics(result).parts_to_create : result.parts_created}
                      tone="green"
                    />
                    <DiagnosticTile
                      label={validateOnly ? 'Parts to update' : 'Parts updated'}
                      value={validateOnly ? getDiagnostics(result).parts_to_update : getDiagnostics(result).parts_to_update}
                      tone="blue"
                    />
                    <DiagnosticTile
                      label={validateOnly ? 'Forecast years' : 'Forecast rows'}
                      value={validateOnly
                        ? (getDiagnostics(result).forecast_years_to_create || 0) + (getDiagnostics(result).forecast_years_to_update || 0)
                        : result.forecasts_created}
                      tone="purple"
                    />
                  </div>

                  {result.dry_run && (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Validation only: no database rows were changed. This file is ready to import.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-red-700 mb-3">
                    {getValidationErrors(result).length || 0} validation error(s) found:
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {getValidationErrors(result).map((err, idx) => (
                      <div
                        key={idx}
                        className="text-sm p-3 bg-red-50 rounded border border-red-200"
                      >
                        <div className="font-medium text-red-900">
                          {err.sheet && `Sheet: ${err.sheet}, `}
                          {err.row && `Row ${err.row}: `}
                          {err.field || err.column}
                        </div>
                        <div className="text-red-700">{err.message}</div>
                        {err.value && (
                          <div className="text-red-600 text-xs mt-1">
                            Value: {JSON.stringify(err.value)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {getWarnings(result).length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-1">Warnings</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {getWarnings(result).map((warning, idx) => (
                        <li key={idx}>
                          {warning.sheet && `${warning.sheet}: `}
                          {warning.message}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {result.diagnostics && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900 mb-3">Import diagnostics</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-slate-500">Opportunity rows:</span> {result.diagnostics.opportunity_rows}</div>
                    <div><span className="text-slate-500">Volume rows:</span> {result.diagnostics.volume_rows}</div>
                    <div><span className="text-slate-500">Unique opportunities:</span> {result.diagnostics.unique_opportunities}</div>
                    <div><span className="text-slate-500">Unique parts:</span> {result.diagnostics.unique_parts}</div>
                    <div><span className="text-slate-500">Forecasts to create:</span> {result.diagnostics.forecast_years_to_create}</div>
                    <div><span className="text-slate-500">Forecasts to update:</span> {result.diagnostics.forecast_years_to_update}</div>
                    <div><span className="text-slate-500">Validation errors:</span> {getValidationErrors(result).length}</div>
                    <div><span className="text-slate-500">Warnings:</span> {getWarnings(result).length}</div>
                  </div>
                </div>
              )}

              {!validateOnly && result.success && (
                <Button
                  onClick={() => (window.location.href = '/opportunities')}
                  className="w-full"
                >
                  View Imported Opportunities
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
